// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Deal, DealStatuses, JacketType } from '@interfaces/deal.interfaces';
import { User, UserTypes } from '@interfaces/user.interfaces';
import { ReasonForRequestStatus } from '@interfaces/vehicle.interfaces';
import { UserService } from '@services/user.service';

@Component({
    selector: 'app-edit-deal-info-jacket-modal',
    templateUrl: './edit-deal-info-modal.component.html',
    styleUrls: ['./edit-deal-info-modal.component.scss'],
})
export class EditDealInfoModalComponent implements OnInit {
    @ViewChild('autoCompleteTrigger') matAutocomplete: MatAutocompleteTrigger;
    public jacketForm: FormGroup;

    public financeManagers = [];
    public filteredFinanceManagers: User[] = [];

    protected readonly JacketType = JacketType;
    protected readonly ReasonForRequestStatuses = Object.entries(ReasonForRequestStatus).map(([key, value]) => ({ key, value })); // Turn enum into a usable array
    protected readonly JacketStatus = DealStatuses;

    constructor(
        private readonly fb: FormBuilder,
        private readonly userService: UserService,
        private dialogRef: MatDialogRef<EditDealInfoModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { deal: Deal }
    ) {}

    get jacketStatus(): string {
        return this.data?.deal?.statusId === DealStatuses.Completed ? 'Closed' : DealStatuses[this.data?.deal?.statusId];
    }

    get reasonForRequest(): { key: string; value: ReasonForRequestStatus } {
        return this.ReasonForRequestStatuses.find(({ key, value }) => value?.toLowerCase() === this.data?.deal?.reasonForRequest?.toLowerCase());
    }

    ngOnInit() {
        this.userService.getDealerUserList(this.data.deal.dealerId, UserTypes.Dealer, '', 1, 100).subscribe(({ users }) => {
            this.financeManagers = users;

            const dealerContact = this.data.deal?.dealerContact;
            const firstName = dealerContact?.firstName || '';
            const lastName = dealerContact?.lastName || '';

            this.filterFinanceManager(firstName + ' ' + lastName, users);
        });

        const createdBy = this.data?.deal?.createdByUser.firstName + ' ' + this.data?.deal?.createdByUser.lastName;
        this.jacketForm = this.fb.group({
            dealId: [{ value: this.data?.deal?.dealId, disabled: true }],
            dealTypeName: [{ value: this.data?.deal?.dealTypeName, disabled: true }],
            dealerDealNumber: [this.data?.deal?.dealerDealNumber || ''],
            financeManager: [{ value: this.data?.deal?.dealerContact, disabled: false }],
            jacketId: [{ value: this.data?.deal?.dealId, disabled: true }],
            jacketStatus: [{ value: this.jacketStatus, disabled: true }],
            jacketType: [this.reasonForRequest?.value || ''],
            createdBy: [{ value: createdBy, disabled: true }],
            deleteReason: [{ value: this.data?.deal?.deleteReason, disabled: false }],
        });

        this.jacketForm.controls.financeManager.valueChanges.subscribe((value) => {
            if (value && typeof value === 'string') {
                this.filterFinanceManager(value);
            }
        });
    }

    displayFinanceManager(user: User): string {
        return user.userId ? `${user.firstName} ${user.lastName}` : 'N/A';
    }

    filterFinanceManager(value: string, users?: User[]) {
        this.filteredFinanceManagers = (users ?? this.financeManagers).filter((user) =>
            `${user.firstName} ${user.lastName}`?.toLowerCase().includes(value?.toLowerCase())
        );
    }

    onEnter() {
        this.matAutocomplete.closePanel();
        this.jacketForm.patchValue(
            {
                financeManager: this.filteredFinanceManagers[0],
            },
            { emitEvent: false }
        );
    }

    submitForm() {
        if (this.jacketForm.valid) {
            const dealFormData = {} as Deal;
            dealFormData.dealerId = this.data?.deal?.dealerId;
            dealFormData.dealId = this.data?.deal?.dealId;
            dealFormData.dealerDealNumber = this.jacketForm.get('dealerDealNumber').value;
            dealFormData.reasonForRequest = this.jacketForm.get('jacketType').value;
            dealFormData.dealerContact = this.jacketForm.get('financeManager').value;
            if (this.data?.deal?.statusId === DealStatuses.Deleted) {
                dealFormData.deleteReason = this.jacketForm.get('deleteReason').value;
            }

            this.dialogRef.close(dealFormData);
        }
    }

    public close() {
        this.dialogRef.close();
    }
}
